import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Check, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useAccess } from "@/hooks/useAccess";
import { useManageSubscription } from "@/hooks/useManageSubscription";
import { SiteHeader } from "@/components/SiteHeader";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: "SmartEssay Pricing — Monthly & Yearly plans for students" },
      { name: "description", content: "Affordable monthly and yearly plans for unlimited framed essay outlines, summaries, and key-concept maps. Cancel anytime." },
      { property: "og:title", content: "SmartEssay Pricing — Plans for students" },
      { property: "og:description", content: "Unlimited framed outlines, summaries, and key concepts. Monthly or yearly. Cancel anytime." },
      { property: "og:url", content: "https://smartessay.ai/pricing" },
      { name: "twitter:title", content: "SmartEssay Pricing" },
      { name: "twitter:description", content: "Monthly and yearly plans. Cancel anytime." },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/pricing" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: "SmartEssay Pro",
        description: "Unlimited framed essay outlines, summaries, and key-concept maps with real cited quotes and APA/MLA exports.",
        brand: { "@type": "Brand", name: "SmartEssay" },
        offers: [
          { "@type": "Offer", name: "Monthly", priceCurrency: "USD", price: "4.99", url: "https://smartessay.ai/pricing", availability: "https://schema.org/InStock" },
          { "@type": "Offer", name: "Yearly", priceCurrency: "USD", price: "49.00", url: "https://smartessay.ai/pricing", availability: "https://schema.org/InStock" },
        ],
      }),
    }],
  }),
});

type Plan = "pro_monthly_499" | "pro_yearly_4900";

function PricingPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Plan | null>(null);
  const access = useAccess(userId);
  const { manage, loading: portalLoading } = useManageSubscription();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserId(data.session?.user.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user.id ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const returnUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;
  }, []);

  if (selected && userId) {
    return (
      <div className="min-h-screen bg-hero-gradient">
        <PaymentTestModeBanner />
        <SiteHeader />
        <div className="mx-auto max-w-5xl px-6 pt-4">
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
            ← Back to plans
          </Button>
        </div>
        <main className="mx-auto max-w-3xl px-6 pb-24">
          <h1 className="font-display text-2xl font-semibold">Complete your purchase</h1>
          <p className="mt-1 text-muted-foreground">
            {selected === "pro_monthly_499" ? "SmartEssay Pro — $4.99/month" : "SmartEssay Pro — $49/year"}
          </p>
          <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
            <StripeEmbeddedCheckout priceId={selected} returnUrl={returnUrl} />
          </div>
        </main>
      </div>
    );
  }

  // Already subscribed → show portal entry instead of plans
  if (userId && !access.loading && access.isSubscribed) {
    return (
      <div className="min-h-screen bg-hero-gradient">
        <PaymentTestModeBanner />
        <SiteHeader />
        <main className="mx-auto max-w-xl px-6 pb-24 pt-10 text-center">
          <h1 className="font-display text-3xl font-semibold tracking-tight">You're subscribed</h1>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            Manage your plan, switch monthly ↔ yearly, update your card, or cancel from the billing portal.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button onClick={manage} disabled={portalLoading} size="lg" className="shadow-glow">
              <Settings className="mr-2 h-4 w-4" />
              {portalLoading ? "Opening…" : "Manage subscription"}
            </Button>
            <Button asChild variant="outline" size="lg"><Link to="/generator">Back to app</Link></Button>
          </div>
        </main>
      </div>
    );
  }

  // If a plan was selected but user is not logged in, redirect to auth
  if (selected && !userId) {
    navigate({ to: "/auth", search: { redirect: "/pricing" } });
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      <PaymentTestModeBanner />
      <SiteHeader />

      <main className="mx-auto max-w-4xl px-6 pb-24 text-center">
        <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Plans</p>
        <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
        <h1 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
          Simple pricing <em>for students</em>
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Free to try • No card needed to start. Upgrade when you need more outlines.
        </p>
        <div className="mx-auto mt-10 grid gap-5 sm:grid-cols-2">
          <PlanCard
            name="Monthly"
            price="$4.99"
            cadence="/month"
            tagline="Cancel anytime."
            features={[
              "Unlimited CER outlines",
              "Essay, summary, and key-concept modes",
              "1 or 2 cited quotes per claim",
              "Intermediate + advanced writing levels",
            ]}
            ctaLabel={userId ? "Subscribe monthly" : "Sign in to subscribe"}
            onClick={() => setSelected("pro_monthly_499")}
          />
          <PlanCard
            name="Yearly"
            price="$49"
            cadence="/year"
            highlight
            tagline="Best value for the school year."
            features={[
              "Everything in Monthly",
              "Billed once a year",
              "Best value for the school year",
            ]}
            ctaLabel={userId ? "Subscribe yearly" : "Sign in to subscribe"}
            onClick={() => setSelected("pro_yearly_4900")}
          />
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-card/60 p-6 text-left shadow-soft">
          <h3 className="font-display text-lg font-semibold">For schools & districts</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            FERPA-compliant licensing, signed DPAs, and volume pricing for classrooms, schools, and districts.
          </p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/contact">Contact us for school plans</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}

function PlanCard({
  name, price, cadence, tagline, features, ctaLabel, onClick, highlight,
}: {
  name: string;
  price: string;
  cadence: string;
  tagline: string;
  features: string[];
  ctaLabel: string;
  onClick: () => void;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col rounded-2xl border bg-card p-6 text-left shadow-soft ${
        highlight ? "border-primary shadow-glow" : "border-border"
      }`}
    >
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg font-semibold">{name}</h2>
        {highlight && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            Best value
          </span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-3xl font-semibold">{price}</span>
        <span className="text-muted-foreground">{cadence}</span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{tagline}</p>
      <ul className="mt-5 space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-none text-primary" /> <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onClick} className="mt-6 w-full" variant={highlight ? "default" : "outline"}>
        {ctaLabel}
      </Button>
    </div>
  );
}
